import { useForm as createUseForm } from "@ant-design-vue/use";
import lodash from "lodash";
import moment from "moment";
import { defineAsyncComponent, reactive, toRaw } from "vue";
import { addModal } from "../../../../../app.logic";
import { createFieldRadioGroup, createFieldSelect } from "../../../../../components/input/template-builder/template-builder.logic";
import i18n from "../../../../../i18n";
import store from "../../../../../store";
import { modalStyleForm } from "../../../../../utils/constant";
import { initUseForm } from "../../../../../utils/form";
import { createLoading } from "../../../../../utils/loading";
import { required, requiredWhen, validateError } from "../../../../../utils/validate";
import { callRequestStatus } from "../request.section";

export const { isLoading, setIsLoading } = createLoading();

export const ConfirmReceiveForm = defineAsyncComponent(() => import("./ConfirmReceiveForm"));

export const callUserApprove = ({ searchQuery, roleName }) => {
    const where = [
        { name: "isActive", type: "Boolean", operator: "=", value: true },
        { name: "isTemp", type: "Boolean", operator: "=", value: false },
        { name: "refPersonType.key", type: "String", operator: "=", value: "2" }
    ];
    if (searchQuery) {
        where.push({ name: "fullNameTh", type: "String", operator: "like", value: searchQuery });
    }
    if (roleName) {
        where.push(
            { openBucket: true, name: "userRoles.refRole.name", type: "String", operator: "=", value: roleName },

            { logic: "OR", openBucket: true, constant: true, name: "1", type: "String", operator: "=", value: "1" }, // 1
            { name: "userInsteadInsteads.refUser.userRoles.refRole.name", type: "String", operator: "=", value: roleName },
            { openBucket: true, constant: true, name: "1", type: "String", operator: "=", value: "1" }, // 2

            { logic: "OR", openBucket: true, constant: true, name: "1", type: "String", operator: "=", value: "1" }, // 3
            { openBucket: true, name: "userInsteadInsteads.startDate", type: "Date", operator: "=", value: null },
            { closeBucket: true, name: "userInsteadInsteads.endDate", type: "Date", operator: "=", value: null },
            { closeBucket: true, constant: true, name: "1", type: "String", operator: "=", value: "1" }, // 3

            { logic: "OR", openBucket: true, constant: true, name: "1", type: "String", operator: "=", value: "1" }, // 3
            { openBucket: true, name: "userInsteadInsteads.startDate", type: "Date", operator: "=", value: null },
            { closeBucket: true, name: "userInsteadInsteads.endDate", type: "Date", operator: ">=", value: moment().format("YYYY-MM-DD") },
            { closeBucket: true, constant: true, name: "1", type: "String", operator: "=", value: "1" }, // 3

            { logic: "OR", openBucket: true, constant: true, name: "1", type: "String", operator: "=", value: "1" }, // 3
            { openBucket: true, name: "userInsteadInsteads.startDate", type: "Date", operator: "<=", value: moment().format("YYYY-MM-DD") },
            { closeBucket: true, name: "userInsteadInsteads.endDate", type: "Date", operator: "=", value: null },
            { closeBucket: true, constant: true, name: "1", type: "String", operator: "=", value: "1" }, // 3

            { logic: "OR", openBucket: true, constant: true, name: "1", type: "String", operator: "=", value: "1" }, // 3
            { openBucket: true, name: "userInsteadInsteads.startDate", type: "Date", operator: "<=", value: moment().format("YYYY-MM-DD") },
            { closeBucket: true, name: "userInsteadInsteads.endDate", type: "Date", operator: ">=", value: moment().format("YYYY-MM-DD") },
            { closeBucket: true, constant: true, name: "1", type: "String", operator: "=", value: "1" }, // 3

            { closeBucket: true, constant: true, name: "1", type: "String", operator: "=", value: "1" }, // 2
            { closeBucket: true, constant: true, name: "1", type: "String", operator: "=", value: "1" } // 1
        );
    }
    return store
        .dispatch("resource/v1/security/user/search", {
            criteria: {
                isDistinct: true,
                select: ["id", "idCard", "fullNameTh", "fullNameEn", "refDepartment.id", "refDepartment.nameTh"],
                page: 0,
                size: 1000000,
                where,
                sort: [{ name: "fullNameTh", type: "ASC" }]
            }
        })
        .then(page => page.data);
};

export const openModalForm = ({ props, onOk, bind }) => {
    const refs = reactive({ form: null });
    const options = addModal({
        bind: lodash.merge(
            lodash.cloneDeep(modalStyleForm),
            {
                okButtonProps: { disabled: false },
                title: i18n.global.t("views.request.modal.receive.title"),
                width: "500px",
                onOk: () => {
                    refs.form.callOnClickModalOk().then(record => {
                        onOk?.(record);
                        options.bind.onCancel();
                    });
                }
            },
            bind
        ),
        render: () => (
            <ConfirmReceiveForm
                ref={e => {
                    if (e) refs.form = e;
                }}
                {...props}
            ></ConfirmReceiveForm>
        )
    });
    return options;
};

export const onClickModalOk = ({ data }) => {
    return data.useForm
        .validate()
        .then(() => ({ ...toRaw(data.useForm.modelRef) }))
        .catch(error => {
            validateError({ error });
            throw error;
        });
};

export const createForm = () => {
    return {
        refStatus: null,
        refToApproveBy: null
    };
};

export const createRules = ({ props, data }) => {
    return {
        refStatus: [required],
        refToApproveBy: [requiredWhen({ when: () => props.visibleRefToApproveBy && data.useForm.modelRef.refStatus?.key === "waitApprove" })]
    };
};

export const createField = ({ props, data }) => {
    const { useForm } = data;
    const { modelRef } = useForm;
    return [
        createFieldRadioGroup({
            key: "refStatus",
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24 }, wrapperCol: { xs: 24 } }),
            inputProp: () => {
                return {
                    value: modelRef.refStatus,
                    "onUpdate:value": value => (modelRef.refStatus = value),
                    list: ({ searchQuery }) =>
                        callRequestStatus({ searchQuery }).then(items => {
                            const result = [];
                            const reEditOfficer = items.find(item => item.key === "reEditOfficer");
                            const waitApprove = items.find(item => item.key === "waitApprove");
                            const notApprove = items.find(item => item.key === "notApprove");
                            if (waitApprove) result.push(waitApprove);
                            if (reEditOfficer) result.push(reEditOfficer);
                            if (notApprove) result.push(notApprove);
                            return result
                                .filter(d => {
                                    if (props.statusKeys?.length > 0) {
                                        return props.statusKeys.includes(d.key);
                                    }
                                    return true;
                                })
                                .map(d => {
                                    if (props.statusDisabledKeys?.length > 0) {
                                        if(props.statusDisabledKeys.includes(d.key)) {
                                            d.disabled = true;
                                        }
                                    }
                                    return d;
                                });
                        }),
                    optLabel: props.statusLabelKey,
                    type: "vertical"
                };
            }
        }),
        createFieldSelect({
            visible: () => props.visibleRefToApproveBy,
            key: "refToApproveBy",
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24 }, wrapperCol: { xs: 24 } }),
            inputProp: () => {
                const disabled = modelRef.refStatus?.key !== "waitApprove";
                if (disabled) {
                    modelRef.refToApproveBy = null;
                }
                return {
                    value: modelRef.refToApproveBy,
                    "onUpdate:value": value => (modelRef.refToApproveBy = value),
                    list: ({ searchQuery }) => callUserApprove({ searchQuery, roleName: props.roleName })
                        .then(data => {
                            if (props?.isShowDepartment && props?.form?.refDepartment?.id) {
                                const dataFilterDepartment = data.filter(d => !d.refDepartment?.id || (d.refDepartment?.id === props?.form?.refDepartment?.id));
                                if (dataFilterDepartment?.length > 0) {
                                    return dataFilterDepartment;
                                }
                            }
                            return data;
                        })
                    ,
                    optLabel: "fullNameTh",
                    renderLabel: ({ item }) => {
                        let label = item.fullNameTh;
                        if (props?.isShowDepartment && item.refDepartment?.nameTh) {
                            label += ` (${item.refDepartment?.nameTh})`
                        }
                        return <div>{label}</div>;
                    },
                    placeholder: i18n.global.t("views.request.modal.receive.refToApproveBy"),
                    disabled
                };
            }
        }),
        {
            visible: () => !!props.remark,
            formItemRender: () => {
                return <div class="text-danger">{props.remark}</div>;
            },
        }
    ];
};

export const loadForm = ({ props, data }) => {
    setIsLoading(true);
    const { form } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    let promise = null;
    if (form) {
        promise = Promise.resolve({ data: [form] });
    } else {
        promise = Promise.resolve({ data: [modelRef] });
    }
    return promise.then(page => initUseForm({ data, page })).finally(() => setIsLoading(false));
};

export const initForm = ({ props, data, refs }) => {
    data.form = createForm({ props, data, refs });
    data.rules = createRules({ props, data, refs });
    data.useForm = createUseForm(data.form, data.rules);
    data.fields = createField({ props, data, refs });
    loadForm({ props, data, refs });
};
